import * as React from 'react';
import {forwardRef} from 'react';
import {FormattedMessage} from 'react-intl';

import {getShowingStartDay, getShowingStartShort, getVIPShowingEnd} from 'features/services/utils/date-helpers';

import './style.scss';

import {A} from '@atoms/LinksAndButtons/A';
import {LinkStyle} from 'features/ui/enums';
import {P} from '@atoms/Text/P';
import {Icon} from '@atoms/Media/Icon';
import {Button, ButtonColor, ButtonSize, ButtonType} from '@atoms/LinksAndButtons/Button';
import {SiteSpecificContent} from '@atoms/SiteSpecificContent';
import {Showing as ShowingType} from "features/search/types";
import {useAuth} from "features/authentication/hooks/useAuth";
import { getFormattedMessage } from 'features/services/utils/language-helper';

interface Props {
    id: string;
    showing: ShowingType;
    isVip?:boolean;
    vipOnly?: boolean;
    smarta?: boolean;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- curried function used both as direct call and as event handler
    onClick?: (showing: ShowingType) => any;
    'data-test-id'?: string;
}

//export const Showing: React.FunctionComponent<Props> = (props) => {
export const Showing = forwardRef<HTMLAnchorElement, Props>(({id, showing, isVip, vipOnly, smarta, onClick}, ref) => {
    const {user} = useAuth();
    const localIsVip = isVip !== undefined ? isVip : user?.isVip;

    // The showing action lives on the row. Expose it on the link too so keyboard users
    // can trigger it (the link was preventDefault-only before); stopPropagation avoids
    // firing the row handler as well on mouse click. See AB#75009.
    const activate = onClick ? onClick(showing) : undefined;
    const handleLinkActivate = (e: React.SyntheticEvent) => {
        e.preventDefault();
        e.stopPropagation();
        activate?.(e);
    };

    const getShowingLinkText = () => {
        if(showing.digitalShowingUrl && vipOnly)
            return "jm.components.Showings.vipLiveShowing";

        if(vipOnly && localIsVip)
            return "jm.components.Showings.vipShowing";

        if(showing.digitalShowingUrl && !vipOnly)
            return "jm.components.Showings.liveShowing";

        return "jm.components.Showings.showing";
    };

    return (
        <div className="showing" onClick={activate}>
            <Icon name="showing" size={16} />

            <P>
                <A
                    id={id}
                    href="#"
                    onClick={handleLinkActivate}
                    variant={!smarta ? LinkStyle.DARK_BG : LinkStyle.DEFAULT}
                    ref={ref}
                    title={getFormattedMessage(getShowingLinkText())} 
                >
                    <strong><FormattedMessage id={getShowingLinkText()}/></strong>
                </A>

                <SiteSpecificContent notFinland>
                    <span x-ms-format-detection="none" className="showing--time">
                        {getShowingStartDay(new Date(showing.startDate))} den {getShowingStartShort(new Date(showing.startDate))}<br/>
                        Kl {getVIPShowingEnd(new Date(showing.startDate))}-{getVIPShowingEnd(new Date(showing.endDate))}<br/>
                    </span>
                </SiteSpecificContent>
                <SiteSpecificContent showOnFinland>
                    <span x-ms-format-detection="none" className="showing--time">
                        {getShowingStartDay(new Date(showing.startDate))} {getShowingStartShort(new Date(showing.startDate))}<br/>
                        Klo {getVIPShowingEnd(new Date(showing.startDate))}-{getVIPShowingEnd(new Date(showing.endDate))}<br/>
                    </span>
                </SiteSpecificContent>

                {showing.digitalShowingUrl
                    ? <A href={showing.digitalShowingUrl} variant={!smarta ? LinkStyle.DARK_BG : LinkStyle.DEFAULT} title={getFormattedMessage('jm.components.Showings.connectToLiveShowing')} >
                        <FormattedMessage id="jm.components.Showings.connectToLiveShowing"/>
                    </A>
                    : <span className="notranslate">{showing.address}, {showing.postalCode} {showing.city}</span>
                }

                <br />
                {localIsVip && !user?.hasBookedShowing(showing.id) && vipOnly && !showing.digitalShowingUrl &&
                    <React.Fragment>
                        <br />
                        <Button
                            className="showing__vip-button"
                            onClick={activate}
                            buttonColor={smarta ? ButtonColor.DEFAULT : ButtonColor.LIGHT} 
                            buttonSize={ButtonSize.DEFAULT} 
                            buttonType={ButtonType.GHOST} 
                        >
                            <FormattedMessage id="jm.components.Showing.bookShowing" />
                        </Button>
                    </React.Fragment>
                }
                {user?.hasBookedShowing(showing.id) &&
                    <React.Fragment>
                        <br />
                        <A
                            href="#"
                            className="showing__booked"
                            variant={!smarta ? LinkStyle.DARK_BG : LinkStyle.DEFAULT}
                            onClick={(e) => {e.preventDefault()}} 
                            title={getFormattedMessage('jm.components.Showing.vipShowingBooked')} 
                        >
                            <FormattedMessage id="jm.components.Showing.vipShowingBooked" />
                        </A>
                    </React.Fragment>
                }
            </P>
        </div>
    )
});
